import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit2, Eye, Filter, X, Loader, User, ChevronLeft, ChevronRight, RefreshCw, AlertCircle, MapPin, Check, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { apiService } from '../../api/api';
import '../Admin.css';

// ✅ ROLE DEFINITIONS
const ROLES = [
  { id: 1, roleName: 'Admin' },
  { id: 2, roleName: 'Customer' },
];
const getRoleId = (roleName) => ROLES.find(r => r.roleName === roleName)?.id ?? 2;
const getRoleName = (roleId) => ROLES.find(r => r.id === parseInt(roleId))?.roleName ?? 'Customer';

// ─────────────────────────────────────────────
// INTERNATIONAL PHONE VALIDATION
// ─────────────────────────────────────────────
const COUNTRY_PHONE_RULES = [
  // North America
  { code: '+1', name: 'USA / Canada', pattern: /^\+1[2-9]\d{2}[2-9]\d{6}$/, digits: '10 digits after +1', example: '+12025551234' },
  { code: '+91', name: 'India', pattern: /^\+91[6-9]\d{9}$/, digits: '10 digits after +91', example: '+919876543210' },
  { code: '+86', name: 'China', pattern: /^\+86(13|14|15|16|17|18|19)\d{9}$/, digits: '11 digits after +86', example: '+8613812345678' },
  { code: '+81', name: 'Japan', pattern: /^\+81[789]0\d{8}$/, digits: '10-11 digits after +81', example: '+819012345678' },
  { code: '+82', name: 'South Korea', pattern: /^\+821[0-9]\d{7,8}$/, digits: '9-10 digits after +82', example: '+821012345678' },
  { code: '+65', name: 'Singapore', pattern: /^\+65[689]\d{7}$/, digits: '8 digits after +65', example: '+6591234567' },
  { code: '+60', name: 'Malaysia', pattern: /^\+601[0-9]\d{7,8}$/, digits: '9-10 digits after +60', example: '+60123456789' },
  { code: '+66', name: 'Thailand', pattern: /^\+66[689]\d{8}$/, digits: '9 digits after +66', example: '+66812345678' },
  { code: '+63', name: 'Philippines', pattern: /^\+639\d{9}$/, digits: '10 digits after +63', example: '+639171234567' },
  { code: '+62', name: 'Indonesia', pattern: /^\+628\d{8,11}$/, digits: '9-12 digits after +62', example: '+62812345678' },
  { code: '+92', name: 'Pakistan', pattern: /^\+923\d{9}$/, digits: '10 digits after +92', example: '+923001234567' },
  { code: '+880', name: 'Bangladesh', pattern: /^\+8801[3-9]\d{8}$/, digits: '10 digits after +880', example: '+8801712345678' },
  // Middle East
  { code: '+971', name: 'UAE', pattern: /^\+9715\d{8}$/, digits: '9 digits after +971', example: '+971501234567' },
  { code: '+966', name: 'Saudi Arabia', pattern: /^\+9665\d{8}$/, digits: '9 digits after +966', example: '+966512345678' },
  { code: '+974', name: 'Qatar', pattern: /^\+974[3-7]\d{7}$/, digits: '8 digits after +974', example: '+97430123456' },
  { code: '+973', name: 'Bahrain', pattern: /^\+973[3-9]\d{7}$/, digits: '8 digits after +973', example: '+97333123456' },
  { code: '+968', name: 'Oman', pattern: /^\+9689\d{7}$/, digits: '8 digits after +968', example: '+96891234567' },
  { code: '+972', name: 'Israel', pattern: /^\+9725\d{8}$/, digits: '9 digits after +972', example: '+972501234567' },
  // Europe
  { code: '+44', name: 'UK', pattern: /^\+447\d{9}$/, digits: '10 digits after +44', example: '+447911123456' },
  { code: '+49', name: 'Germany', pattern: /^\+491[567]\d{8,10}$/, digits: '10-12 digits after +49', example: '+4915123456789' },
  { code: '+33', name: 'France', pattern: /^\+33[67]\d{8}$/, digits: '9 digits after +33', example: '+33612345678' },
  { code: '+39', name: 'Italy', pattern: /^\+393\d{9}$/, digits: '10 digits after +39', example: '+393123456789' },
  { code: '+34', name: 'Spain', pattern: /^\+346\d{8}$/, digits: '9 digits after +34', example: '+34612345678' },
  { code: '+31', name: 'Netherlands', pattern: /^\+316\d{8}$/, digits: '9 digits after +31', example: '+31612345678' },
  { code: '+41', name: 'Switzerland', pattern: /^\+417[6-9]\d{7}$/, digits: '9 digits after +41', example: '+41761234567' },
  { code: '+43', name: 'Austria', pattern: /^\+436\d{8,10}$/, digits: '9-11 digits after +43', example: '+436641234567' },
  { code: '+32', name: 'Belgium', pattern: /^\+324\d{8}$/, digits: '9 digits after +32', example: '+32412345678' },
  { code: '+47', name: 'Norway', pattern: /^\+474\d{7}$/, digits: '8 digits after +47', example: '+4741234567' },
  { code: '+46', name: 'Sweden', pattern: /^\+467\d{8}$/, digits: '9 digits after +46', example: '+46701234567' },
  { code: '+45', name: 'Denmark', pattern: /^\+454\d{7}$/, digits: '8 digits after +45', example: '+4541234567' },
  { code: '+358', name: 'Finland', pattern: /^\+3584\d{7,8}$/, digits: '8-9 digits after +358', example: '+358412345678' },
  { code: '+48', name: 'Poland', pattern: /^\+48[4-9]\d{8}$/, digits: '9 digits after +48', example: '+48512345678' },
  { code: '+7', name: 'Russia', pattern: /^\+7[789]\d{9}$/, digits: '10 digits after +7', example: '+79161234567' },
  { code: '+380', name: 'Ukraine', pattern: /^\+380[3-9]\d{8}$/, digits: '9 digits after +380', example: '+380501234567' },
  { code: '+30', name: 'Greece', pattern: /^\+306\d{9}$/, digits: '10 digits after +30', example: '+306901234567' },
  { code: '+351', name: 'Portugal', pattern: /^\+3519\d{8}$/, digits: '9 digits after +351', example: '+351912345678' },
  { code: '+36', name: 'Hungary', pattern: /^\+36[237]\d{7,8}$/, digits: '8-9 digits after +36', example: '+36201234567' },
  { code: '+420', name: 'Czech Republic', pattern: /^\+420[6-7]\d{8}$/, digits: '9 digits after +420', example: '+420601234567' },
  { code: '+40', name: 'Romania', pattern: /^\+407\d{8}$/, digits: '9 digits after +40', example: '+40712345678' },
  // Americas
  { code: '+55', name: 'Brazil', pattern: /^\+55[1-9][1-9]\d{8,9}$/, digits: '10-11 digits after +55', example: '+5511912345678' },
  { code: '+52', name: 'Mexico', pattern: /^\+521\d{10}$/, digits: '10 digits after +52', example: '+5215512345678' },
  { code: '+54', name: 'Argentina', pattern: /^\+549\d{10}$/, digits: '10 digits after +54', example: '+5491112345678' },
  { code: '+56', name: 'Chile', pattern: /^\+569\d{8}$/, digits: '9 digits after +56', example: '+56912345678' },
  { code: '+57', name: 'Colombia', pattern: /^\+573\d{9}$/, digits: '10 digits after +57', example: '+573001234567' },
  { code: '+27', name: 'South Africa', pattern: /^\+276\d{8}$/, digits: '9 digits after +27', example: '+27612345678' },
  { code: '+234', name: 'Nigeria', pattern: /^\+2347\d{9}$/, digits: '10 digits after +234', example: '+2347012345678' },
  { code: '+20', name: 'Egypt', pattern: /^\+201[0125]\d{8}$/, digits: '10 digits after +20', example: '+201012345678' },
  { code: '+254', name: 'Kenya', pattern: /^\+2547\d{8}$/, digits: '9 digits after +254', example: '+254712345678' },
  { code: '+61', name: 'Australia', pattern: /^\+614\d{8}$/, digits: '9 digits after +61', example: '+61412345678' },
  { code: '+64', name: 'New Zealand', pattern: /^\+642\d{7,9}$/, digits: '8-10 digits after +64', example: '+6421234567' },
];

// Detect country rule from phone number
const detectCountryRule = (phone) => {
  if (!phone || !phone.startsWith('+')) return null;
  // Sort by longest code first to avoid false matches (e.g. +1 vs +91)
  const sorted = [...COUNTRY_PHONE_RULES].sort((a, b) => b.code.length - a.code.length);
  return sorted.find(r => phone.startsWith(r.code)) || null;
};

// Validate international phone number
const validatePhoneNumber = (phone) => {
  if (!phone || !phone.trim()) return { valid: false, message: 'Phone number is required' };

  const cleaned = phone.replace(/[\s\-().]/g, '');

  if (!cleaned.startsWith('+')) {
    return {
      valid: false,
      message: 'Phone number must start with a country code (e.g. +1 for USA, +91 for India, +86 for China)',
    };
  }

  const rule = detectCountryRule(cleaned);
  if (!rule) {
    // Generic fallback: must be +[1-4 digit country code][6-12 digits]
    const genericPattern = /^\+\d{1,4}\d{6,12}$/;
    if (!genericPattern.test(cleaned)) {
      return { valid: false, message: 'Invalid phone number. Use format: +[country code][number] (e.g. +12025551234)' };
    }
    return { valid: true, message: '', country: null };
  }

  if (!rule.pattern.test(cleaned)) {
    return {
      valid: false,
      message: `Invalid ${rule.name} number. Format: ${rule.digits} (e.g. ${rule.example})`,
      country: rule,
    };
  }

  return { valid: true, message: '', country: rule };
};

// Phone input component with country detection hint + real-time filtering
const PhoneInput = ({ value, onChange, onBlur, error, style }) => {
  const cleaned = value?.replace(/[\s\-().]/g, '') || '';
  const detected = detectCountryRule(cleaned);

  // Allow only: +, digits (0-9), space, dash, dot, parentheses
  const ALLOWED_PHONE_CHARS = /^[+\d\s\-().]*$/;

  const handleKeyDown = (e) => {
    // Always allow: Backspace, Delete, Tab, Enter, Arrow keys, Home, End, Ctrl/Cmd combos
    if (
      e.key === 'Backspace' || e.key === 'Delete' ||
      e.key === 'Tab' || e.key === 'Enter' ||
      e.key === 'ArrowLeft' || e.key === 'ArrowRight' ||
      e.key === 'ArrowUp' || e.key === 'ArrowDown' ||
      e.key === 'Home' || e.key === 'End' ||
      e.ctrlKey || e.metaKey
    ) return;

    // Block + if it's not the very first character (cursor at pos 0)
    if (e.key === '+') {
      const input = e.target;
      if (input.selectionStart !== 0 || value.includes('+')) {
        e.preventDefault();
        return;
      }
    }

    // Block any character that's not a digit, space, dash, dot, parenthesis
    if (!/[\d\s\-().]/.test(e.key) && e.key !== '+') {
      e.preventDefault();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    // Strip everything except allowed chars, then enforce + only at start
    let filtered = pasted.replace(/[^+\d\s\-().]/g, '');
    // Move any + to the start and remove extras
    const hasPlus = filtered.includes('+');
    filtered = filtered.replace(/\+/g, '');
    if (hasPlus) filtered = '+' + filtered;
    // Merge with existing value at cursor
    const input = e.target;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const newVal = value.slice(0, start) + filtered + value.slice(end);
    onChange({ target: { name: 'phoneNumber', value: newVal } });
  };

  const handleChange = (e) => {
    const val = e.target.value;
    // As a final safety net: strip any character not in allowed set
    if (!ALLOWED_PHONE_CHARS.test(val)) {
      const safe = val.replace(/[^+\d\s\-().]/g, '');
      onChange({ target: { name: 'phoneNumber', value: safe } });
      return;
    }
    // Ensure + only appears at position 0
    if (val.indexOf('+') > 0 || (val.split('+').length - 1) > 1) {
      const digits = val.replace(/\+/g, '');
      onChange({ target: { name: 'phoneNumber', value: '+' + digits } });
      return;
    }
    onChange(e);
  };

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        {detected && (
          <span style={{
            position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
            fontSize: 18, pointerEvents: 'none', zIndex: 1, userSelect: 'none'
          }}>
            {detected.flag}
          </span>
        )}
        <input
          type="tel"
          name="phoneNumber"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={onBlur}
          placeholder="+1 202 555 1234  or  +91 98765 43210  or  +86 138 1234 5678"
          style={{
            ...style,
            paddingLeft: detected ? 38 : 12,
            transition: 'padding 0.2s',
          }}
        />
      </div>
      {/* Detected country label */}
      {detected && !error && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 4, marginTop: 4,
          fontSize: 11, color: '#16a34a', fontWeight: 600,
        }}>
          <Check size={11} />
          Detected: {detected.flag} {detected.name} · {detected.digits}
        </div>
      )}
      {/* Examples hint */}
      {!detected && value && value.startsWith('+') && !error && (
        <div style={{ marginTop: 4, fontSize: 11, color: '#6b7280' }}>
          🌍 Examples: +12025551234 (USA) · +919876543210 (India) · +8613812345678 (China)
        </div>
      )}
      {!value && (
        <div style={{ marginTop: 4, fontSize: 11, color: '#9ca3af' }}>
          Must start with country code · e.g. +1 (USA), +91 (India), +86 (China), +44 (UK)
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// BLANK ADDRESS TEMPLATE
// ─────────────────────────────────────────────
const blankAddress = () => ({
  fullName: '', mobileNo: '', alternateMobileNo: '',
  addressLine1: '', addressLine2: '', landmark: '',
  city: '', state: '', pincode: '', country: 'USA',
  isDefault: false, isBilling: false, isShipping: false,
});

const ADDRESS_FIELDS = [
  { label: 'Full Name', name: 'fullName', span: false },
  { label: 'Mobile No.', name: 'mobileNo', span: false },
  { label: 'Alternate Mobile', name: 'alternateMobileNo', span: false },
  { label: 'Address Line 1', name: 'addressLine1', span: true },
  { label: 'Address Line 2', name: 'addressLine2', span: true },
  { label: 'Landmark', name: 'landmark', span: false },
  { label: 'City', name: 'city', span: false },
  { label: 'State', name: 'state', span: false },
  { label: 'Pincode', name: 'pincode', span: false },
  // { label: 'Country', name: 'country', span: false },
];

const ADDRESS_CHECKBOXES = [
  { name: 'isDefault', label: 'Set as Default' },
  { name: 'isBilling', label: 'Billing Address' },
  { name: 'isShipping', label: 'Shipping Address' },
];

// ─────────────────────────────────────────────
// NEW ADDRESS CARD
// ─────────────────────────────────────────────
const NewAddressCard = ({ address, index, onChange, onRemove }) => {
  const [open, setOpen] = useState(index === 0);
  const ch = (e) => {
    const { name, value, type, checked } = e.target;
    onChange(index, { ...address, [name]: type === 'checkbox' ? checked : value });
  };
  const toggleCheck = (name) => onChange(index, { ...address, [name]: !address[name] });
  const summary = [address.addressLine1, address.city, address.state].filter(Boolean).join(', ') || 'No details yet';

  return (
    <div style={{ border: '1.5px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', marginBottom: 10, background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', gap: 10, cursor: 'pointer', background: open ? '#f8faff' : '#fff' }} onClick={() => setOpen(o => !o)}>
        <div style={{ flex: 1, minWidth: 0 }} onClick={() => setOpen(o => !o)}>
          <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b', marginBottom: 2 }}>{address.fullName || `Address ${index + 1}`}</div>
          <div style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summary}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }} onClick={() => setOpen(o => !o)}>
          {address.isDefault && <span style={{ fontSize: 10, fontWeight: 700, background: '#dbeafe', color: '#1d4ed8', borderRadius: 6, padding: '2px 7px' }}>Default</span>}
          {address.isBilling && <span style={{ fontSize: 10, fontWeight: 700, background: '#dcfce7', color: '#15803d', borderRadius: 6, padding: '2px 7px' }}>Billing</span>}
          {address.isShipping && <span style={{ fontSize: 10, fontWeight: 700, background: '#fef9c3', color: '#a16207', borderRadius: 6, padding: '2px 7px' }}>Shipping</span>}
        </div>
        <button type="button" onClick={(e) => { e.stopPropagation(); onRemove(index); }} title="Remove address" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f87171', padding: '4px', borderRadius: 6, display: 'flex', alignItems: 'center', flexShrink: 0, transition: 'background 0.15s' }} onMouseEnter={e => e.currentTarget.style.background = '#fee2e2'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
          <Trash2 size={15} />
        </button>
        <div style={{ cursor: 'pointer', color: '#94a3b8', display: 'flex' }} onClick={() => setOpen(o => !o)}>
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>
      {open && (
        <div style={{ padding: '14px 16px', borderTop: '1px solid #f1f5f9', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 14px' }}>
          {ADDRESS_FIELDS.map(({ label, name, span }) => (
            <div key={name} style={{ gridColumn: span ? '1 / -1' : undefined }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>{label}</label>
              <input name={name} value={address[name]} onChange={ch} style={{ width: '100%', padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} onFocus={e => (e.target.style.borderColor = '#3b82f6')} onBlur={e => (e.target.style.borderColor = '#e2e8f0')} />
            </div>
          ))}
          <div>
            <label style={{
              fontSize: 11,
              fontWeight: 600,
              color: '#64748b',
              display: 'block',
              marginBottom: 4
            }}>
              Country
            </label>

            <input
              value="USA"
              readOnly
              style={{
                width: '100%',
                padding: '7px 10px',
                border: '1.5px solid #e2e8f0',
                borderRadius: 7,
                fontSize: 13,
                background: '#f8fafc',
                cursor: 'not-allowed'
              }}
            />
          </div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 4 }}>
            {ADDRESS_CHECKBOXES.map(({ name, label }) => (
              <label key={name} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 13, color: '#374151', fontWeight: 500, userSelect: 'none' }}>
                <div onClick={() => toggleCheck(name)} style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${address[name] ? '#3b82f6' : '#d1d5db'}`, background: address[name] ? '#3b82f6' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s' }}>
                  {address[name] && <Check size={11} color="white" strokeWidth={3} />}
                </div>
                {label}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// ADDRESS EDIT CARD
// ─────────────────────────────────────────────
const AddressEditCard = ({ address, index, onSave }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...address });
  const ch = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };
  const save = async () => {
    setSaving(true);
    try { await onSave(address.id, form); setOpen(false); }
    catch { /* error handled in parent */ }
    finally { setSaving(false); }
  };

  return (
    <div style={{ border: '1.5px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', marginBottom: 10, background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', gap: 10, cursor: 'pointer', background: open ? '#f8faff' : '#fff' }} onClick={() => setOpen(o => !o)}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b', marginBottom: 2 }}>{address.fullName || `Address ${index + 1}`}</div>
          <div style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {[address.addressLine1, address.city, address.state].filter(Boolean).join(', ') || 'No details'}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {address.isDefault && <span style={{ fontSize: 10, fontWeight: 700, background: '#dbeafe', color: '#1d4ed8', borderRadius: 6, padding: '2px 7px' }}>Default</span>}
          {address.isBilling && <span style={{ fontSize: 10, fontWeight: 700, background: '#dcfce7', color: '#15803d', borderRadius: 6, padding: '2px 7px' }}>Billing</span>}
          {address.isShipping && <span style={{ fontSize: 10, fontWeight: 700, background: '#fef9c3', color: '#a16207', borderRadius: 6, padding: '2px 7px' }}>Shipping</span>}
        </div>
        <div style={{ cursor: 'pointer', color: '#94a3b8', display: 'flex' }}>
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>
      {open && (
        <div style={{ padding: '14px 16px', borderTop: '1px solid #f1f5f9' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 14px' }}>
            {ADDRESS_FIELDS.map(({ label, name, span }) => (
              <div key={name} style={{ gridColumn: span ? '1 / -1' : undefined }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>{label}</label>
                <input value="USA" readOnly />
              </div>
            ))}
            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 4 }}>
              {ADDRESS_CHECKBOXES.map(({ name, label }) => (
                <label key={name} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 13, color: '#374151', fontWeight: 500, userSelect: 'none' }}>
                  <div onClick={() => setForm(p => ({ ...p, [name]: !p[name] }))} style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${form[name] ? '#3b82f6' : '#d1d5db'}`, background: form[name] ? '#3b82f6' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s' }}>
                    {form[name] && <Check size={11} color="white" strokeWidth={3} />}
                  </div>
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
            <button onClick={() => { setOpen(false); setForm({ ...address }); }} style={{ padding: '7px 16px', border: '1.5px solid #e2e8f0', borderRadius: 6, background: '#fff', color: '#6b7280', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
            <button onClick={save} disabled={saving} style={{ padding: '7px 18px', border: 'none', borderRadius: 6, background: saving ? '#93c5fd' : '#3b82f6', color: '#fff', fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              {saving ? <><Loader size={13} style={{ animation: 'spin 1s linear infinite' }} /> Saving...</> : <>Save Address</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// ADDRESS VIEW CARD
// ─────────────────────────────────────────────
const AddressViewCard = ({ address, index }) => (
  <div style={{ border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '12px 16px', background: '#fafbff', marginBottom: 10 }}>
    <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
      {address.isDefault && <span style={{ fontSize: 10, fontWeight: 700, background: '#dbeafe', color: '#1d4ed8', borderRadius: 6, padding: '2px 7px' }}>Default</span>}
      {address.isBilling && <span style={{ fontSize: 10, fontWeight: 700, background: '#dcfce7', color: '#15803d', borderRadius: 6, padding: '2px 7px' }}>Billing</span>}
      {address.isShipping && <span style={{ fontSize: 10, fontWeight: 700, background: '#fef9c3', color: '#a16207', borderRadius: 6, padding: '2px 7px' }}>Shipping</span>}
      {!address.isDefault && !address.isBilling && !address.isShipping && (
        <span style={{ fontSize: 10, fontWeight: 700, background: '#f3f4f6', color: '#6b7280', borderRadius: 6, padding: '2px 7px' }}>Address {index + 1}</span>
      )}
    </div>
    <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b', marginBottom: 4 }}>{address.fullName || 'N/A'}</div>
    {address.mobileNo && <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>📞 {address.mobileNo}</div>}
    <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.7 }}>
      {[address.addressLine1, address.addressLine2, address.landmark && `Near: ${address.landmark}`, [address.city, address.state, address.pincode].filter(Boolean).join(', '), address.country].filter(Boolean).map((l, i) => <div key={i}>{l}</div>)}
    </div>
    {address.alternateMobileNo && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>Alt: {address.alternateMobileNo}</div>}
  </div>
);

// ─────────────────────────────────────────────
// INFO ROW
// ─────────────────────────────────────────────
const InfoRow = ({ label, value, badge, badgeColor, badgeBg }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
    <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>{label}</span>
    {badge
      ? <span style={{ fontSize: 12, fontWeight: 700, background: badgeBg, color: badgeColor, borderRadius: 20, padding: '3px 12px' }}>{value}</span>
      : <span style={{ fontSize: 13, color: '#1e293b', fontWeight: 600 }}>{value || '—'}</span>
    }
  </div>
);

// ─────────────────────────────────────────────
// ADDRESSES SECTION
// ─────────────────────────────────────────────
const AddressesSection = ({ addresses, addressLoading, mode, onSave }) => (
  <div style={{ marginTop: 20 }}>
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12, gap: 6 }}>
      <MapPin size={15} color="#4361ee" />
      <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>Saved Addresses</span>
      {!addressLoading && (
        <span style={{ fontSize: 10, fontWeight: 700, background: addresses.length > 0 ? '#dbeafe' : '#f3f4f6', color: addresses.length > 0 ? '#1d4ed8' : '#9ca3af', padding: '1px 7px', borderRadius: 10, marginLeft: 4 }}>
          {addresses.length}
        </span>
      )}
      {mode === 'edit' && !addressLoading && addresses.length > 0 && (
        <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 4 }}>— click to expand & edit</span>
      )}
    </div>
    {addressLoading && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px', color: '#6b7280', fontSize: 13 }}>
        <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading addresses...
      </div>
    )}
    {!addressLoading && addresses.length === 0 && (
      <div style={{ padding: '14px 16px', background: '#f8fafc', borderRadius: 8, border: '1.5px dashed #e2e8f0', color: '#94a3b8', fontSize: 13, textAlign: 'center' }}>
        No saved addresses for this user
      </div>
    )}
    {!addressLoading && addresses.map((addr, i) =>
      mode === 'edit'
        ? <AddressEditCard key={addr.id || i} address={addr} index={i} onSave={onSave} />
        : <AddressViewCard key={addr.id || i} address={addr} index={i} />
    )}
  </div>
);

// ─────────────────────────────────────────────
// NEW ADDRESSES SECTION
// ─────────────────────────────────────────────
const NewAddressesSection = ({ addresses, onChange, onAdd, onRemove }) => (
  <div style={{ marginTop: 20 }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <MapPin size={15} color="#4361ee" />
        <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>Addresses</span>
        <span style={{ fontSize: 10, fontWeight: 700, background: addresses.length > 0 ? '#dbeafe' : '#f3f4f6', color: addresses.length > 0 ? '#1d4ed8' : '#9ca3af', padding: '1px 7px', borderRadius: 10, marginLeft: 2 }}>
          {addresses.length}
        </span>
        <span style={{ fontSize: 11, color: '#94a3b8' }}>— optional</span>
      </div>
      <button type="button" onClick={onAdd} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', border: '1.5px solid #4361ee', borderRadius: 7, background: '#fff', color: '#4361ee', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
        <Plus size={13} /> Add Address
      </button>
    </div>
    {addresses.length === 0 && (
      <div style={{ padding: '14px 16px', background: '#f8fafc', borderRadius: 8, border: '1.5px dashed #e2e8f0', color: '#94a3b8', fontSize: 13, textAlign: 'center' }}>
        No addresses added — click Add Address to attach one
      </div>
    )}
    {addresses.map((addr, i) => (
      <NewAddressCard key={i} address={addr} index={i} onChange={onChange} onRemove={onRemove} />
    ))}
  </div>
);

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
const UsersPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedUser, setSelectedUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    id: 'string', name: '', gender: '', phoneNumber: '', email: '', role: 'Customer'
  });
  const [errors, setErrors] = useState({});

  // Existing user addresses (edit / view mode)
  const [addresses, setAddresses] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  // New addresses drafted in add mode
  const [newAddresses, setNewAddresses] = useState([]);

  useEffect(() => { fetchUsers(); }, []);
  useEffect(() => { setCurrentPage(1); }, [searchTerm, filterRole]);

  // ── Fetch users ──────────────────────────────────────────────────────
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getAllUsers();
      let usersData = [];
      if (Array.isArray(response)) usersData = response;
      else if (response && Array.isArray(response.data)) usersData = response.data;
      else if (response && Array.isArray(response.users)) usersData = response.users;

      if (usersData.length === 0) { setUsers([]); setLoading(false); return; }

      const usersWithRoles = await Promise.all(
        usersData.map(async (user) => {
          let roleName = 'Customer', roleId = 2;
          try {
            const roleData = await apiService.getUserRole(user.id);
            if (roleData) {
              const extracted = roleData.roleId !== undefined ? roleData.roleId
                : roleData.RoleId !== undefined ? roleData.RoleId
                  : roleData.role_id !== undefined ? roleData.role_id
                    : typeof roleData === 'number' ? roleData : null;
              if (extracted != null) { roleId = parseInt(extracted); roleName = getRoleName(roleId); }
            }
          } catch { }
          return {
            ...user, role: roleName, roleId,
            displayName: user.name || 'N/A',
            displayEmail: user.email || 'N/A',
            displayPhone: user.phoneNumber || 'N/A',
            displayGender: user.gender || 'N/A',
          };
        })
      );
      setUsers(usersWithRoles);
      setError(null);
      setRetryCount(0);
    } catch (err) {
      let msg = 'Failed to load users. ';
      if (err.response?.status === 401) msg = 'Session expired. Please login again.';
      else if (err.response?.status === 403) msg = 'Access denied. You do not have permission.';
      else if (err.response?.status === 404) msg = 'User API endpoint not found.';
      else if (err.response?.status === 500) msg = 'Server error. Please try again later.';
      else if (!err.response) msg = 'Network error. Check your connection.';
      else msg += err.response?.data?.message || err.message;
      setError(msg);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // ── Fetch addresses ──────────────────────────────────────────────────
  const fetchUserAddresses = async (userId) => {
    if (!userId || userId === 'string') { setAddresses([]); return; }
    setAddressLoading(true);
    try {
      const r = await apiService.getUserAddresses(userId);
      setAddresses(Array.isArray(r) ? r : []);
    } catch { setAddresses([]); }
    finally { setAddressLoading(false); }
  };

  // ── Save existing address (edit mode) ───────────────────────────────
  const handleSaveAddress = async (addressId, data) => {
    try {
      await apiService.updateAddress(addressId, data);
      await fetchUserAddresses(selectedUser?.id);
      alert('Address updated successfully!');
    } catch (err) {
      alert('Failed to update address: ' + (err.response?.data?.message || err.message));
      throw err;
    }
  };

  // ── Save new addresses for a newly-created user ──────────────────────
  const saveNewAddressesForUser = async (userId) => {
    const filledAddresses = newAddresses.filter(a => a.fullName || a.addressLine1 || a.city || a.mobileNo);
    if (filledAddresses.length === 0) return;
    const results = await Promise.allSettled(
      filledAddresses.map(addr => apiService.post('/UserAddresses', {
        userId: parseInt(userId),
        fullName: addr.fullName || '', mobileNo: addr.mobileNo || '',
        alternateMobileNo: addr.alternateMobileNo || '', email: addr.email || '',
        addressLine1: addr.addressLine1 || '', addressLine2: addr.addressLine2 || '',
        isDefault: Boolean(addr.isDefault), isBilling: Boolean(addr.isBilling),
        isShipping: Boolean(addr.isShipping), landmark: addr.landmark || '',
        city: addr.city || '', state: addr.state || '',
        country: 'USA', pincode: addr.pincode || '',
      }))
    );
    const failed = results.filter(r => r.status === 'rejected').length;
    if (failed > 0) console.warn(`⚠️ ${failed} of ${filledAddresses.length} address(es) failed to save.`);
  };

  const handleRetry = () => { setRetryCount(p => p + 1); fetchUsers(); };

  // ── newAddress helpers ───────────────────────────────────────────────
  const handleNewAddressChange = (index, updated) => setNewAddresses(prev => prev.map((a, i) => i === index ? updated : a));
  const handleAddNewAddress = () => setNewAddresses(prev => [...prev, blankAddress()]);
  const handleRemoveNewAddress = (index) => setNewAddresses(prev => prev.filter((_, i) => i !== index));

  // ── Filtering ────────────────────────────────────────────────────────
  const filteredUsers = users.filter(u => {
    const ms = u.displayName?.toLowerCase().includes(searchTerm.toLowerCase())
      || u.displayEmail?.toLowerCase().includes(searchTerm.toLowerCase())
      || u.displayPhone?.toLowerCase().includes(searchTerm.toLowerCase());
    return ms && (filterRole === 'all' || u.role === filterRole);
  });
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentUsers = filteredUsers.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (p) => {
    if (p >= 1 && p <= totalPages) { setCurrentPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }
  };

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) { for (let i = 1; i <= totalPages; i++) pages.push(i); }
    else if (currentPage <= 3) { for (let i = 1; i <= 4; i++) pages.push(i); pages.push('...'); pages.push(totalPages); }
    else if (currentPage >= totalPages - 2) { pages.push(1); pages.push('...'); for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i); }
    else { pages.push(1); pages.push('...'); pages.push(currentPage - 1); pages.push(currentPage); pages.push(currentPage + 1); pages.push('...'); pages.push(totalPages); }
    return pages;
  };

  // ── Modal helpers ────────────────────────────────────────────────────
  const resetForm = () => setFormData({ id: 'string', name: '', gender: '', phoneNumber: '', email: '', role: 'Customer' });

  const openAddModal = () => {
    setModalMode('add'); resetForm(); setErrors({}); setSelectedUser(null);
    setAddresses([]); setNewAddresses([]); setShowModal(true);
  };
  const openEditModal = (user) => {
    setModalMode('edit');
    setFormData({ id: user.id || 'string', name: user.name || '', gender: user.gender || '', phoneNumber: user.phoneNumber || '', email: user.email || '', role: user.role || 'Customer' });
    setErrors({}); setSelectedUser(user); setAddresses([]); setNewAddresses([]);
    setShowModal(true); fetchUserAddresses(user.id);
  };
  const openViewModal = (user) => {
    setModalMode('view');
    setFormData({ id: user.id || '', name: user.name || '', gender: user.gender || '', phoneNumber: user.phoneNumber || '', email: user.email || '', role: user.role || 'Customer' });
    setSelectedUser(user); setAddresses([]); setNewAddresses([]);
    setShowModal(true); fetchUserAddresses(user.id);
  };
  const closeModal = () => {
    setShowModal(false); resetForm(); setErrors({});
    setSelectedUser(null); setAddresses([]); setNewAddresses([]);
  };

  // ── Validation ───────────────────────────────────────────────────────
  const validateForm = () => {
    const e = {};

    if (!formData.name.trim()) e.name = 'Name is required';

    // ── Email validation with duplicate check ──
    if (!formData.email.trim()) {
      e.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      e.email = 'Email is invalid';
    } else if (modalMode === 'add') {
      const duplicate = users.find(
        u => u.email?.toLowerCase() === formData.email.trim().toLowerCase()
      );
      if (duplicate) e.email = `A user with email "${formData.email.trim()}" already exists.`;
    } else if (modalMode === 'edit') {
      const duplicate = users.find(
        u => u.email?.toLowerCase() === formData.email.trim().toLowerCase() && u.id !== formData.id
      );
      if (duplicate) e.email = `A user with email "${formData.email.trim()}" already exists.`;
    }

    // ── International phone validation ──
    if (!formData.phoneNumber.trim()) {
      e.phoneNumber = 'Phone number is required';
    } else {
      const phoneResult = validatePhoneNumber(formData.phoneNumber.trim());
      if (!phoneResult.valid) {
        e.phoneNumber = phoneResult.message;
      }
    }

    if (!formData.gender) e.gender = 'Gender is required';
    if (!formData.role) e.role = 'Role is required';

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Submit ───────────────────────────────────────────────────────────
  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const payload = {
        id: formData.id,
        name: formData.name,
        gender: formData.gender,
        phoneNumber: formData.phoneNumber,
        email: formData.email,
        password: 'string',
        roleId: getRoleId(formData.role),
      };

      if (modalMode === 'add') {
        await apiService.saveUser(payload);
        try {
          const allUsers = await apiService.getAllUsers();
          let usersArray = [];
          if (Array.isArray(allUsers)) usersArray = allUsers;
          else if (Array.isArray(allUsers?.data)) usersArray = allUsers.data;
          else if (Array.isArray(allUsers?.users)) usersArray = allUsers.users;
          const saved = usersArray.find(u => u.email?.toLowerCase() === formData.email.toLowerCase());
          if (saved) {
            const rawId = saved.id ?? saved.userId ?? saved.ID ?? saved.Id;
            const newUserId = parseInt(rawId, 10);
            if (!isNaN(newUserId) && newUserId > 0) await saveNewAddressesForUser(newUserId);
          }
        } catch (addrErr) { console.warn('⚠️ Address creation failed (user was still saved):', addrErr); }
        await fetchUsers();

      } else if (modalMode === 'edit') {
        await apiService.updateUser(payload);
        if (newAddresses.length > 0) {
          try {
            const filledNew = newAddresses.filter(a => a.fullName || a.addressLine1 || a.city || a.mobileNo);
            if (filledNew.length > 0) {
              await Promise.allSettled(
                filledNew.map(addr => apiService.post('/UserAddresses', {
                  userId: parseInt(formData.id),
                  fullName: addr.fullName || '', mobileNo: addr.mobileNo || '',
                  alternateMobileNo: addr.alternateMobileNo || '', email: addr.email || '',
                  addressLine1: addr.addressLine1 || '', addressLine2: addr.addressLine2 || '',
                  isDefault: Boolean(addr.isDefault), isBilling: Boolean(addr.isBilling),
                  isShipping: Boolean(addr.isShipping), landmark: addr.landmark || '',
                  city: addr.city || '', state: addr.state || '',
                  country: addr.country || '', pincode: addr.pincode || '',
                }))
              );
            }
          } catch (addrErr) { console.warn('⚠️ New address save failed (user still updated):', addrErr); }
        }
        setUsers(prevUsers => prevUsers.map(user => {
          if (user.id === formData.id) {
            return {
              ...user, name: formData.name, email: formData.email,
              phoneNumber: formData.phoneNumber, gender: formData.gender,
              role: formData.role, roleId: getRoleId(formData.role),
              displayName: formData.name, displayEmail: formData.email,
              displayPhone: formData.phoneNumber, displayGender: formData.gender,
            };
          }
          return user;
        }));
      }

      closeModal();
      alert(`User ${modalMode === 'add' ? 'added' : 'updated'} successfully!`);

    } catch (err) {
      if (err.response?.status === 401) {
        alert('Session expired.');
        window.location.href = '/login';
        return;
      }

      // ── Detect duplicate email from API response ──
      const apiMsg = (err.response?.data?.message || err.message || '').toLowerCase();
      const isDuplicateEmail =
        err.response?.status === 409 ||
        (apiMsg.includes('email') && (apiMsg.includes('exist') || apiMsg.includes('duplicate') || apiMsg.includes('taken') || apiMsg.includes('already')));

      if (isDuplicateEmail) {
        setErrors(prev => ({ ...prev, email: `A user with email "${formData.email}" already exists.` }));
        document.getElementById('email-field')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      // ── Detect duplicate phone from API response ──
      const isDuplicatePhone =
        apiMsg.includes('phone') && (apiMsg.includes('exist') || apiMsg.includes('duplicate') || apiMsg.includes('taken') || apiMsg.includes('already'));

      if (isDuplicatePhone) {
        setErrors(prev => ({ ...prev, phoneNumber: `A user with phone number "${formData.phoneNumber}" already exists.` }));
        document.getElementById('phone-field')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      alert(`Failed to ${modalMode === 'add' ? 'add' : 'update'} user: ${err.response?.data?.message || err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]: value }));
    if (errors[name]) setErrors(p => ({ ...p, [name]: '' }));
  };

  const stats = {
    total: users.length,
    customers: users.filter(u => u.role === 'Customer').length,
    admins: users.filter(u => u.role === 'Admin').length,
  };

  // ──────────────────────────────────────────────────────────────────────
  // LOADING
  // ──────────────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 16 }}>
      <Loader size={40} color="#4361ee" style={{ animation: 'spin 1s linear infinite' }} />
      <div style={{ fontSize: 16, color: '#4361ee', fontWeight: 600 }}>Loading users...</div>
      <div style={{ fontSize: 13, color: '#94a3b8' }}>Fetching roles for each user...</div>
    </div>
  );

  // ──────────────────────────────────────────────────────────────────────
  // ERROR
  // ──────────────────────────────────────────────────────────────────────
  if (error) {
    const isAuth = error.includes('expired') || error.includes('login') || error.includes('Session');
    return (
      <div style={{ padding: '32px 24px' }}>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1e293b', margin: 0 }}>User Management</h1>
          <p style={{ color: '#64748b', margin: '4px 0 0' }}>Manage all users and their accounts</p>
        </div>
        <div style={{ background: '#fff', borderRadius: 12, border: '1.5px solid #fee2e2', padding: 32, textAlign: 'center', maxWidth: 480, margin: '0 auto' }}>
          {isAuth ? <AlertCircle size={48} color="#ef4444" style={{ marginBottom: 16 }} /> : <RefreshCw size={48} color="#f59e0b" style={{ marginBottom: 16 }} />}
          <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: '0 0 8px' }}>Unable to Load Users</h3>
          <p style={{ color: '#64748b', fontSize: 14, margin: '0 0 24px' }}>{error}</p>
          {isAuth
            ? <button onClick={() => window.location.href = '/login'} style={{ padding: '10px 24px', background: '#4361ee', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Login to Continue</button>
            : <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={handleRetry} disabled={retryCount >= 3} style={{ padding: '10px 24px', background: retryCount >= 3 ? '#e2e8f0' : '#4361ee', color: retryCount >= 3 ? '#94a3b8' : '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: retryCount >= 3 ? 'not-allowed' : 'pointer' }}>
                {retryCount >= 3 ? 'Max Retries Reached' : 'Retry'}
              </button>
              <button onClick={() => window.location.href = '/admin'} style={{ padding: '10px 24px', background: '#f1f5f9', color: '#4a5568', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Go to Dashboard</button>
            </div>
          }
          {retryCount > 0 && retryCount < 3 && <div style={{ marginTop: 12, fontSize: 12, color: '#f59e0b' }}>Retry attempt {retryCount} of 3</div>}
        </div>
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────────────────
  // MAIN RENDER
  // ──────────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1e293b', margin: 0 }}>User Management</h1>
          <p style={{ color: '#64748b', margin: '4px 0 0' }}>Manage all users and their accounts</p>
        </div>
        <button onClick={openAddModal} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: 'rgb(1, 141, 113)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          <Plus size={16} /> Add New User
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Total Users', val: stats.total, sub: 'All registered users', color: '#4361ee' },
          { label: 'Customers', val: stats.customers, sub: 'Customer accounts', color: '#f59e0b' },
          { label: 'Admins', val: stats.admins, sub: 'Admin accounts', color: '#8b5cf6' },
        ].map(({ label, val, sub, color }) => (
          <div key={label} style={{ background: '#fff', borderRadius: 10, padding: '18px 20px', border: '1.5px solid #e2e8f0' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>{label}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color, marginBottom: 2 }}>{val}</div>
            <div style={{ fontSize: 12, color: '#94a3b8' }}>{sub}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240, position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input placeholder="Search users..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ width: '100%', padding: '9px 12px 9px 36px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
        </div>
        <div style={{ position: 'relative' }}>
          <Filter size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }} />
          <select value={filterRole} onChange={e => setFilterRole(e.target.value)} style={{ padding: '9px 12px 9px 30px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, outline: 'none', background: 'white', cursor: 'pointer' }}>
            <option value="all">All Roles</option>
            {ROLES.map(r => <option key={r.id} value={r.roleName}>{r.roleName}</option>)}
          </select>
        </div>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1.5px solid #e2e8f0', overflow: 'hidden' }}>
        {users.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <User size={48} color="#e2e8f0" style={{ marginBottom: 16 }} />
            <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1e293b', margin: '0 0 8px' }}>No Users Yet</h3>
            <p style={{ color: '#94a3b8', fontSize: 14 }}>Click "Add New User" to create your first user</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <Search size={48} color="#e2e8f0" style={{ marginBottom: 16 }} />
            <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1e293b', margin: '0 0 8px' }}>No Users Found</h3>
            <p style={{ color: '#94a3b8', fontSize: 14 }}>Try adjusting your search or filter criteria</p>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap', gap: 8 }}>
              <span style={{ fontSize: 13, color: '#64748b' }}>
                Showing {startIndex + 1}–{Math.min(startIndex + itemsPerPage, filteredUsers.length)} of {filteredUsers.length} users
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, color: '#64748b' }}>Per page:</span>
                <select value={itemsPerPage} onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, cursor: 'pointer', background: 'white' }}>
                  {[5, 10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['ID', 'Name', 'Email', 'Phone', 'Gender', 'Role', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#64748b', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentUsers.map((user, idx) => (
                    <tr key={user.id || idx} style={{ borderBottom: '1px solid #f1f5f9' }} onMouseEnter={e => e.currentTarget.style.background = '#fafbff'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: '#64748b' }}>{user.id || 'N/A'}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#4361ee', flexShrink: 0 }}>
                            {user.displayName?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{user.displayName}</span>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: '#64748b' }}>{user.displayEmail}</td>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: '#64748b', whiteSpace: 'nowrap' }}>{user.displayPhone}</td>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: '#64748b' }}>{user.displayGender}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, background: user.role === 'Admin' ? '#ede9fe' : '#fef9c3', color: user.role === 'Admin' ? '#7c3aed' : '#a16207', borderRadius: 20, padding: '3px 10px' }}>
                          {user.role || 'Customer'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button title="View" onClick={() => openViewModal(user)} style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#64748b' }}><Eye size={14} /></button>
                          <button title="Edit" onClick={() => openEditModal(user)} style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#4361ee' }}><Edit2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, padding: '16px', borderTop: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, background: currentPage === 1 ? '#f7fafc' : 'white', color: currentPage === 1 ? '#cbd5e0' : '#4a5568', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 14, fontWeight: 500 }}>
                  <ChevronLeft size={14} /> Previous
                </button>
                {getPageNumbers().map((page, i) =>
                  page === '...'
                    ? <span key={i} style={{ padding: '8px 4px', color: '#94a3b8' }}>...</span>
                    : <button key={i} onClick={() => handlePageChange(page)} style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, background: currentPage === page ? '#4361ee' : 'white', color: currentPage === page ? 'white' : '#4a5568', cursor: 'pointer', fontSize: 14, fontWeight: currentPage === page ? 600 : 500, minWidth: 40 }}>{page}</button>
                )}
                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, background: currentPage === totalPages ? '#f7fafc' : 'white', color: currentPage === totalPages ? '#cbd5e0' : '#4a5568', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 14, fontWeight: 500 }}>
                  Next <ChevronRight size={14} />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ═══════════════════════ MODAL ═══════════════════════ */}
      {showModal && (
        <div onClick={closeModal} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16, backdropFilter: 'blur(2px)' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxWidth: 720, width: '95%', maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1e293b' }}>
                {modalMode === 'add' && 'Add New User'}
                {modalMode === 'edit' && 'Edit User'}
                {modalMode === 'view' && 'User Details'}
              </h2>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4, borderRadius: 6, display: 'flex' }}><X size={20} /></button>
            </div>

            {/* ════ VIEW MODE ════ */}
            {modalMode === 'view' && (
              <div style={{ overflowY: 'auto', flex: 1 }}>
                <div style={{ background: 'linear-gradient(135deg, #6bbd7d 0%, #4e8059 100%)', padding: '28px 24px', textAlign: 'center' }}>
                  <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 800, color: '#fff', margin: '0 auto 12px', border: '3px solid rgba(255,255,255,0.3)' }}>
                    {formData.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#fff', marginBottom: 4 }}>{formData.name || 'N/A'}</div>
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', marginBottom: 10 }}>{formData.email}</div>
                  <span style={{ fontSize: 12, fontWeight: 700, background: 'rgba(255,255,255,0.25)', color: '#fff', borderRadius: 20, padding: '4px 14px' }}>{formData.role}</span>
                </div>
                <div style={{ padding: '20px 24px' }}>
                  <InfoRow label="Full Name" value={formData.name} />
                  <InfoRow label="Email Address" value={formData.email} />
                  <InfoRow label="Phone Number" value={formData.phoneNumber} />
                  <InfoRow label="Gender" value={formData.gender} />
                  <InfoRow label="Role" value={formData.role} badge badgeColor={formData.role === 'Admin' ? '#7c3aed' : '#a16207'} badgeBg={formData.role === 'Admin' ? '#ede9fe' : '#fef9c3'} />
                  <AddressesSection addresses={addresses} addressLoading={addressLoading} mode="view" />
                </div>
                <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0 }}>
                  <button onClick={closeModal} style={{ padding: '9px 20px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: '#fff', color: '#64748b', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Close</button>
                  <button onClick={() => openEditModal(selectedUser)} style={{ padding: '9px 20px', border: 'none', borderRadius: 8, background: '#018D71', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Edit2 size={14} /> Edit User
                  </button>
                </div>
              </div>
            )}

            {/* ════ ADD / EDIT MODE ════ */}
            {(modalMode === 'add' || modalMode === 'edit') && (
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                <div style={{ overflowY: 'auto', flex: 1, padding: '20px 24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18, padding: '10px 14px', background: '#f8faff', borderRadius: 8, border: '1px solid #e0e7ff' }}>
                    <User size={15} color="#4361ee" />
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#4361ee' }}>User Information</span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 20px' }}>
                    {/* Name */}
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Name *</label>
                      <input name="name" value={formData.name} onChange={handleInputChange} placeholder="Full name" style={{ width: '100%', padding: '9px 12px', border: `1.5px solid ${errors.name ? '#f87171' : '#e2e8f0'}`, borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} onFocus={e => e.target.style.borderColor = '#4361ee'} onBlur={e => e.target.style.borderColor = errors.name ? '#f87171' : '#e2e8f0'} />
                      {errors.name && <span style={{ fontSize: 11, color: '#ef4444', marginTop: 4, display: 'block' }}>{errors.name}</span>}
                    </div>

                    {/* Email */}
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Email *</label>
                      <input id="email-field" type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="user@example.com" style={{ width: '100%', padding: '9px 12px', border: `1.5px solid ${errors.email ? '#f87171' : '#e2e8f0'}`, borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} onFocus={e => e.target.style.borderColor = '#4361ee'} onBlur={e => e.target.style.borderColor = errors.email ? '#f87171' : '#e2e8f0'} />
                      {errors.email && <span style={{ fontSize: 11, color: '#ef4444', marginTop: 4, display: 'block' }}>⚠️ {errors.email}</span>}
                    </div>

                    {/* Phone */}
                    <div style={{ gridColumn: '1 / -1' }} id="phone-field">
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Phone Number *</label>
                      <PhoneInput
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        onBlur={() => {
                          if (formData.phoneNumber) {
                            const result = validatePhoneNumber(formData.phoneNumber);
                            if (!result.valid) setErrors(p => ({ ...p, phoneNumber: result.message }));
                            else setErrors(p => ({ ...p, phoneNumber: '' }));
                          }
                        }}
                        error={errors.phoneNumber}
                        style={{
                          width: '100%',
                          padding: '9px 12px',
                          border: `1.5px solid ${errors.phoneNumber ? '#f87171' : '#e2e8f0'}`,
                          borderRadius: 8,
                          fontSize: 14,
                          outline: 'none',
                          boxSizing: 'border-box',
                        }}
                      />
                      {errors.phoneNumber && <span style={{ fontSize: 11, color: '#ef4444', marginTop: 4, display: 'block' }}>⚠️ {errors.phoneNumber}</span>}
                    </div>

                    {/* Gender */}
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Gender *</label>
                      <select name="gender" value={formData.gender} onChange={handleInputChange} style={{ width: '100%', padding: '9px 12px', border: `1.5px solid ${errors.gender ? '#f87171' : '#e2e8f0'}`, borderRadius: 8, fontSize: 14, outline: 'none', background: 'white', boxSizing: 'border-box' }}>
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                      {errors.gender && <span style={{ fontSize: 11, color: '#ef4444', marginTop: 4, display: 'block' }}>{errors.gender}</span>}
                    </div>

                    {/* Role */}
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Role *</label>
                      <select name="role" value={formData.role} onChange={handleInputChange} style={{ width: '100%', padding: '9px 12px', border: `1.5px solid ${errors.role ? '#f87171' : '#e2e8f0'}`, borderRadius: 8, fontSize: 14, outline: 'none', background: 'white', boxSizing: 'border-box' }}>
                        <option value="">Select Role</option>
                        {ROLES.map(r => <option key={r.id} value={r.roleName}>{r.roleName}</option>)}
                      </select>
                      {errors.role && <span style={{ fontSize: 11, color: '#ef4444', marginTop: 4, display: 'block' }}>{errors.role}</span>}
                    </div>
                  </div>

                  {/* Add mode: draft address builder */}
                  {modalMode === 'add' && (
                    <NewAddressesSection
                      addresses={newAddresses}
                      onChange={handleNewAddressChange}
                      onAdd={handleAddNewAddress}
                      onRemove={handleRemoveNewAddress}
                    />
                  )}

                  {/* Edit mode: existing addresses + add new */}
                  {modalMode === 'edit' && (
                    <div>
                      <AddressesSection addresses={addresses} addressLoading={addressLoading} mode="edit" onSave={handleSaveAddress} />
                      <div style={{ marginTop: 12 }}>
                        <NewAddressesSection
                          addresses={newAddresses}
                          onChange={handleNewAddressChange}
                          onAdd={handleAddNewAddress}
                          onRemove={handleRemoveNewAddress}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0, background: '#fff' }}>
                  <button type="button" onClick={closeModal} style={{ padding: '9px 20px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: '#fff', color: '#64748b', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                  <button
                    type="submit"
                    disabled={submitting}
                    style={{
                      padding: '9px 22px',
                      border: 'none',
                      borderRadius: 8,
                      background: submitting ? 'rgb(1, 141, 113, 0.5)' : 'rgb(1, 141, 113)',
                      color: '#fff',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: submitting ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}
                  >
                    {submitting
                      ? (
                        <>
                          <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
                          {modalMode === 'add' ? 'Adding...' : 'Updating...'}
                        </>
                      )
                      : (
                        <>
                          {modalMode === 'add'
                            ? ('Add User' + (newAddresses.length > 0
                              ? ' + ' + newAddresses.length + ' Address' + (newAddresses.length > 1 ? 'es' : '')
                              : ''))
                            : ('Update User' + (newAddresses.length > 0
                              ? ' + ' + newAddresses.length + ' New Address' + (newAddresses.length > 1 ? 'es' : '')
                              : ''))
                          }
                        </>
                      )
                    }
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;